<template>
  <canvas ref="canvas" class="absolute inset-0 w-full h-full pointer-events-none" />
</template>

<script>
import * as THREE from 'three';

const NUM_POINTS = 200;

export default {
  name: 'OceanNoiseRipple',
  props: {
    image: { type: String, required: true },
  },
  data() {
    return {
      points: [],
    };
  },
  mounted() {
    this.initThree();
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
    cancelAnimationFrame(this.rafId);
  },
  methods: {
    initThree() {
      const canvas = this.$refs.canvas;
      if (!canvas || !canvas.parentElement) {
        console.error('Canvas or parent element not found');
        return;
      }

      console.log('Initializing Voronoi noise...');
      this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true });
      this.scene = new THREE.Scene();
      this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

      // Initialize NUM_POINTS random points with varying speeds
      this.points = [];
      for (let i = 0; i < NUM_POINTS; i++) {
        this.points.push({
          x: Math.random(),
          y: Math.random(),
          speedY: 0.0002 + Math.random() * 0.00008, // Downward speed (0.0002 to 0.0002)
          speedX: (Math.random() - 0.5) * 0.0008, // Horizontal drift (-0.0001 to 0.0001)
        });
      }
      console.log('Generated', this.points.length, 'points');

      this.uniforms = {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2() },
        uPointsTexture: { value: null },
        uNumPoints: { value: NUM_POINTS },
      };

      // Create a data texture to store point positions
      const pointsData = new Float32Array(NUM_POINTS * 4); // RGBA for each point
      for (let i = 0; i < this.points.length; i++) {
        pointsData[i * 4] = this.points[i].x;
        pointsData[i * 4 + 1] = this.points[i].y;
        pointsData[i * 4 + 2] = 0; // unused
        pointsData[i * 4 + 3] = 1; // unused
      }
      this.pointsTexture = new THREE.DataTexture(
        pointsData,
        NUM_POINTS,
        1,
        THREE.RGBAFormat,
        THREE.FloatType,
      );
      this.pointsTexture.needsUpdate = true;
      this.uniforms.uPointsTexture.value = this.pointsTexture;

      this.handleResize();
      this.createMesh();

      // Try to resize again after a tick in case parent wasn't ready
      setTimeout(() => {
        this.handleResize();
      }, 100);

      this.animate();
      console.log('Voronoi animation started');
    },
    createMesh() {
      if (this.mesh) {
        this.scene.remove(this.mesh);
        this.mesh.geometry.dispose();
        this.mesh.material.dispose();
      }
      const geometry = new THREE.PlaneGeometry(2, 2);
      const material = new THREE.ShaderMaterial({
        uniforms: this.uniforms,
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform float uTime;
          uniform vec2 uResolution;
          uniform sampler2D uPointsTexture;
          uniform float uNumPoints;
          varying vec2 vUv;
          
          void main() {
            vec2 uv = vUv;
            float aspect = uResolution.x / uResolution.y;
            vec2 uvAspect = vec2(uv.x, uv.y / aspect);
            float minDist1 = 999999.0;
            float minDist2 = 999999.0;
            float minDist3 = 999999.0;
            for (int i = 0; i < int(uNumPoints); i++) {
              vec2 point = texture2D(uPointsTexture, vec2(float(i) / float(uNumPoints), 0.5)).xy;
              vec2 pointAspect = vec2(point.x, point.y / aspect);
              float dist = pow(distance(uvAspect, pointAspect), 1.08); // aspect-corrected distance
              if (dist < minDist1) {
                minDist3 = minDist2;
                minDist2 = minDist1;
                minDist1 = dist;
              } else if (dist < minDist2) {
                minDist3 = minDist2;
                minDist2 = dist;
              } else if (dist < minDist3) {
                minDist3 = dist;
              }
            }

            // Edge/corner detection
            float edgeDist12 = minDist2 - minDist1;
            float edgeDist23 = minDist3 - minDist2;

            // Edge: where two cells meet
            float edge = 1.0 - smoothstep(0.0, 0.025, edgeDist12);
            // Corner: where three cells meet (sharper)
            float corner = 1.0 - smoothstep(0.0, 0.09, edgeDist23);

            // Make the transition to white more gradual, only the sharpest corners reach full brightness
            float edgeAlpha = pow(edge, 2.5) * 0.10; // faint regular edge
            float cornerAlpha = pow(edge * corner, 14.0) * 1.0; // only the most perfect corners reach white
            float alpha = max(edgeAlpha, cornerAlpha);

            vec3 color = vec3(1.0);
            gl_FragColor = vec4(color, alpha*0.7);
          }
        `,
        transparent: true,
        blending: THREE.NormalBlending,
        depthWrite: false,
      });

      // Check for shader compilation errors
      if (material.fragmentShader && material.vertexShader) {
        console.log('Shader material created successfully');
      }

      this.mesh = new THREE.Mesh(geometry, material);
      this.scene.add(this.mesh);
      console.log('Mesh added to scene');
    },
    animate() {
      // Update point positions
      const pointsData = this.pointsTexture.image.data;
      for (let i = 0; i < this.points.length; i++) {
        const point = this.points[i];

        // Move point downwards and horizontally
        point.y -= point.speedY * 0.4;
        point.x += point.speedX;

        // Wrap vertically: if below canvas (y > 1), wrap to top (y = 0)
        if (point.y < 0.0) {
          point.y = 1.0;
        }

        // Wrap horizontally to keep points on screen
        if (point.x < 0.0) {
          point.x = 1.0;
        } else if (point.x > 1.0) {
          point.x = 0.0;
        }

        // Update texture data
        pointsData[i * 4] = point.x;
        pointsData[i * 4 + 1] = point.y;
      }

      this.pointsTexture.needsUpdate = true;

      this.uniforms.uTime.value = performance.now() / 1000;
      this.renderer.render(this.scene, this.camera);
      this.rafId = requestAnimationFrame(this.animate);
    },
    handleResize() {
      const canvas = this.$refs.canvas;
      if (!canvas || !canvas.parentElement) return;

      const dpr = window.devicePixelRatio || 1;
      const width = canvas.parentElement.offsetWidth || window.innerWidth;
      const height = canvas.parentElement.offsetHeight || window.innerHeight;

      console.log('Canvas size:', width, 'x', height);

      if (width === 0 || height === 0) {
        console.warn('Canvas parent has 0 dimensions, using window size');
        return;
      }

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      this.renderer.setSize(width, height, false);
      this.uniforms.uResolution.value.set(width, height);
    },
  },
};
</script>

<style scoped>
canvas {
  display: block;
}
</style>
